import { CategoryOptInCookie } from '@snapchat/mw-cookie-components';
import isEqual from 'lodash-es/isEqual';

import { Config } from '../../config';
import type { Personas } from '../../types/personas';
import type { CookieManager } from '../cookies/CookieManager';
import { personasCookieKey } from './personasConstants';

/**
 * Gets the personas cookie value as JSON.
 *
 * @param cookieManager - The cookie manager instance used to read cookies
 * @returns - Returns the personas object from the cookie if found and parsed successfully
 */
export function getPersonasCookie(cookieManager: CookieManager) {
  return cookieManager.getCookieJson(personasCookieKey) as Personas | undefined;
}

/**
 * Sets the personas cookie if the user has accepted preferences cookies.
 *
 * @param cookieManager - The cookie manager instance used to read and set cookies
 * @param personas - The personas data to store, or undefined if no personas are available
 * @returns Void - Returns early if preferences cookie is not accepted
 */
export function setPersonasCookie(cookieManager: CookieManager, personas: Personas | undefined) {
  // If a profile has been looked up previously, then 'personas' should be an empty object
  if (!personas) {
    return;
  }

  const preferencesCookieValue = cookieManager.getCookie(CategoryOptInCookie.Preferences);

  // Do nothing if the user has not accepted preferences cookies
  if (preferencesCookieValue !== 'true') {
    return;
  }

  const personasCookieValue = getPersonasCookie(cookieManager);

  // If the existing cookie value is the same as the new value, no need to set the cookie again
  if (isEqual(personas, personasCookieValue)) {
    return;
  }

  // If the user has accepted preferences, set the personas cookie
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1); // 1 year from now

  // Use empty string for non-production environments so cookies can be set on localhost
  const domain =
    !Config.isDeploymentTypeProd || !Config.isCompilationModeProd
      ? ''
      : Config.trackingSettings.cookieDomain;

  cookieManager.setCookieJson(personasCookieKey, personas as any, {
    secure: true,
    expires,
    sameSite: 'strict',
    domain,
  });
}
